import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, map } from 'rxjs';
import { Booking, BookingStatus } from '../../../core/entities/booking/booking.entity';
import { environment } from '../../../../environments/environment';
import { DateTime } from 'luxon';
import {PendingBooking} from '../../entities/booking/pending-booking.entity';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private readonly refreshBookings$ = new Subject<void>();
  public constructor(
    private readonly http: HttpClient,
  ) {}

  public createBooking(
    customerEmail: string,
    customerFirstname: string | null,
    customerLastname: string | null,
    serviceId: number,
    datetime: DateTime,
    deposit?: number | null,
  ): Observable<Booking> {
    const datetimeUtcIso = datetime.toUTC().toISO();

    const payload: Record<string, unknown> = {
      customerEmail,
      customerFirstname,
      customerLastname,
      serviceId,
      datetime: datetimeUtcIso
    };

    if (typeof deposit === 'number' && !isNaN(deposit)) {
      payload['deposit'] = Math.round(deposit);
    }

    return this.http.post<Booking>(`${environment.apiUrl}/api/v1/manage/booking`, payload).pipe(
      map(mapBooking)
    );
  }

  public getBookingsOfPhotographer(): Observable<Booking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/manage/booking`)
      .pipe(map(mapBookings));
  }

  public getOngoingBookingsOfPhotographer(): Observable<Booking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/manage/booking/ongoing`)
      .pipe(map(mapBookings));
  }

  public getPastBookingsOfPhotographer(): Observable<Booking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/manage/booking/past`)
      .pipe(map(mapBookings));
  }

  public getPendingBookings(): Observable<PendingBooking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/manage/booking/pending`)
      .pipe(map(mapPendingBookings));
  }

  public getBooking(id: number): Observable<Booking> {
    return this.http.get<any>(`${environment.apiUrl}/api/v1/manage/booking/${id}`)
      .pipe(map(mapBooking));
  }

  public changeBookingStatus(id: number, status: BookingStatus): Observable<Booking> {
    return this.http.patch<any>(`${environment.apiUrl}/api/v1/manage/booking/${id}/status`, {
      status
    }).pipe(map(mapBooking));
  }

  public onRefreshBookings(): Observable<void> {
    return this.refreshBookings$.asObservable();
  }

  public triggerRefreshBookings(): void {
    this.refreshBookings$.next();
  }
}

// Helpers de mapping

export function mapBooking(raw: any): Booking {
  return {
    ...raw,
    depositPercent: raw.depositPercent ?? null,
    depositAmount: raw.depositAmount ?? null,
    depositStatus: raw.depositStatus ?? null,
    balanceAmount: raw.balanceAmount ?? null,
    balanceStatus: raw.balanceStatus ?? null,
    balanceDueAt: raw.balanceDueAt ? DateTime.fromISO(raw.balanceDueAt) : null,
    paymentBreakdown: Array.isArray(raw.paymentBreakdown)
      ? raw.paymentBreakdown.map((item: any) => ({
        ...item,
        dueAt: item?.dueAt ? DateTime.fromISO(item.dueAt) : null
      }))
      : [],
    datetime: DateTime.fromISO(raw.datetime),
    createdAt: raw.createdAt ? DateTime.fromISO(raw.createdAt) : null,
    updatedAt: raw.updatedAt ? DateTime.fromISO(raw.updatedAt) : null,
    deletedAt: raw.deletedAt ? DateTime.fromISO(raw.deletedAt) : null,
  };
}

export function mapBookings(rawList: any[]): Booking[] {
  return rawList.map(mapBooking);
}

export function mapPendingBooking(raw: any): PendingBooking {
  return {
    id: raw.id,
    serviceName: raw.serviceName ?? '',
    datetime: raw.datetime ? DateTime.fromISO(raw.datetime) : null,
    price: raw.price ?? 0,
  };
}

export function mapPendingBookings(rawList: any[]): PendingBooking[] {
  return rawList.map(mapPendingBooking);
}
